import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Sparkles, Copy, Check, ChevronDown, ChevronRight, Cpu,
  Pencil, Save, X, RotateCcw, Lock, FileDiff, AlertCircle,
} from 'lucide-react';
import { api } from '@/shared/api';

interface PromptDescriptor {
  id: string;
  module: string;
  stage: string;
  model: string;
  role: 'system' | 'user' | 'deterministic';
  title: string;
  description: string;
  default_body: string;
  override_body: string | null;
  chars: number;
  editable: boolean;
  not_editable_reason: string | null;
  override?: {
    id: string;
    version: number;
    change_note: string | null;
    created_by: string;
    created_at: string;
  };
}

interface PromptsResponse {
  prompts: PromptDescriptor[];
  models: { reasoning: string; extraction: string };
}

export function PromptsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'prompts'],
    queryFn: () => api.get<PromptsResponse>('/api/admin/prompts').then(r => r.data),
    staleTime: 60_000,
  });

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight text-ink-strong inline-flex items-center gap-2">
          <Sparkles className="w-7 h-7" style={{ color: 'var(--accent)' }} />
          Prompts
        </h1>
        <p className="text-sm text-ink-400 mt-1.5 tracking-tight max-w-3xl">
          The exact system and user prompts sent to Claude at every stage of the pipeline. Edit a prompt to customize behavior — changes apply on the next Claude call (typically within 60 seconds). Reset reverts to the compiled-in default.
        </p>
      </div>

      {data?.models && (
        <div className="grid grid-cols-2 gap-3 mb-6">
          <ModelChip label="Reasoning" model={data.models.reasoning} />
          <ModelChip label="Extraction" model={data.models.extraction} />
        </div>
      )}

      {isLoading && (
        <div className="card p-12 text-center text-ink-400">Loading prompts…</div>
      )}

      {data?.prompts.map((p, idx) => (
        <PromptCard key={p.id} prompt={p} defaultOpen={idx < 2} />
      ))}
    </div>
  );
}

function ModelChip({ label, model }: { label: string; model: string }) {
  return (
    <div className="card p-3 flex items-center gap-3">
      <Cpu className="w-5 h-5" style={{ color: 'var(--accent)' }} />
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-ink-400">{label}</div>
        <div className="text-sm font-mono text-ink-strong mt-0.5">{model}</div>
      </div>
    </div>
  );
}

function PromptCard({ prompt, defaultOpen }: { prompt: PromptDescriptor; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [draft, setDraft] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  const activeBody = prompt.override_body ?? prompt.default_body;
  const isOverridden = prompt.override_body !== null;
  const queryClient = useQueryClient();

  const saveMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.put<{ ok: true }>(`/api/admin/prompts/${prompt.id}`, {
        body: draft,
        change_note: changeNote || null,
      });
      return data;
    },
    onSuccess: () => {
      setEditing(false);
      setChangeNote('');
      setSaveError(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'prompts'] });
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: string; message?: string } } };
      setSaveError(e.response?.data?.message ?? e.response?.data?.error ?? 'Save failed.');
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.delete<{ ok: true }>(`/api/admin/prompts/${prompt.id}`);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'prompts'] });
    },
  });

  const copy = (): void => {
    void navigator.clipboard.writeText(editing ? draft : activeBody);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const startEdit = (): void => {
    setDraft(activeBody);
    setChangeNote('');
    setSaveError(null);
    setEditing(true);
    setShowDiff(false);
    setOpen(true);
  };

  const cancelEdit = (): void => {
    setEditing(false);
    setDraft('');
    setChangeNote('');
    setSaveError(null);
  };

  const draftChanged = draft !== activeBody;
  const draftEmpty = !draft.trim();

  const diffLines = useMemo(() => {
    if (!showDiff) return null;
    const left = prompt.default_body.split('\n');
    const right = (editing ? draft : activeBody).split('\n');
    const max = Math.max(left.length, right.length);
    const lines: Array<{ left: string | null; right: string | null; changed: boolean }> = [];
    for (let i = 0; i < max; i++) {
      const L = i < left.length ? left[i] : null;
      const R = i < right.length ? right[i] : null;
      lines.push({ left: L, right: R, changed: L !== R });
    }
    return lines;
  }, [showDiff, prompt.default_body, draft, activeBody, editing]);

  return (
    <div className="card mb-4 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full px-5 py-4 flex items-start gap-3 text-left transition"
        style={{ background: open ? 'var(--bg-hover)' : 'transparent' }}
      >
        <div className="mt-0.5 text-ink-400">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`pill ${
              prompt.role === 'system' ? 'pill-accent'
              : prompt.role === 'user' ? 'pill-info'
              : 'pill-neutral'
            }`}>
              {prompt.role === 'deterministic' ? 'code (no AI)' : prompt.role}
            </span>
            <span className="text-xs text-ink-400 font-mono">{prompt.model}</span>
            <span className="text-xs text-ink-600">·</span>
            <span className="text-xs text-ink-400">{prompt.stage}</span>
            {isOverridden && (
              <span className="pill"
                    style={{
                      background: 'color-mix(in srgb, var(--status-warn) 12%, transparent)',
                      borderColor: 'color-mix(in srgb, var(--status-warn) 40%, transparent)',
                      color: 'var(--status-warn)',
                    }}>
                Custom · v{prompt.override?.version}
              </span>
            )}
            {!prompt.editable && (
              <span className="inline-flex items-center gap-1 text-xs text-ink-400">
                <Lock className="w-3 h-3" />
                read-only
              </span>
            )}
          </div>
          <div className="text-base font-semibold text-ink-strong tracking-tight">{prompt.title}</div>
          <div className="text-xs text-ink-400 mt-1 tracking-tight">{prompt.description}</div>
        </div>
        <div className="text-right text-xs text-ink-400 font-mono shrink-0 ml-2 tabular-nums">
          {prompt.chars.toLocaleString()} chars
          <div className="text-[10px] text-ink-600">~{Math.round(prompt.chars / 4).toLocaleString()} tokens</div>
        </div>
      </button>

      {open && (
        <div className="border-t" style={{ borderColor: 'var(--glass-border)' }}>
          <div className="flex items-center justify-between px-5 py-2 gap-2"
               style={{ background: 'var(--bg-input)' }}>
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-[0.18em] text-ink-400">
                {editing ? 'editing'
                 : prompt.role === 'deterministic' ? 'parser logic (deterministic)'
                 : `${prompt.role} prompt body`}
              </span>
              {isOverridden && prompt.override && !editing && (
                <span className="text-[10px] text-ink-400 font-mono">
                  v{prompt.override.version} · by {prompt.override.created_by.slice(0, 8)} ·{' '}
                  {new Date(prompt.override.created_at).toLocaleString()}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {(isOverridden || editing) && (
                <button
                  type="button"
                  onClick={() => setShowDiff(s => !s)}
                  className="text-xs text-ink-200 hover:text-ink-strong inline-flex items-center gap-1.5 px-2 py-1 rounded transition"
                  style={{ background: showDiff ? 'var(--bg-hover)' : 'transparent' }}
                  title="Compare against the compiled-in default"
                >
                  <FileDiff className="w-3.5 h-3.5" />
                  Diff
                </button>
              )}
              <button
                type="button"
                onClick={copy}
                className="text-xs text-ink-200 hover:text-ink-strong inline-flex items-center gap-1.5 px-2 py-1 rounded transition"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-status-ok" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
              {!editing && prompt.editable && (
                <button
                  type="button"
                  onClick={startEdit}
                  className="text-xs inline-flex items-center gap-1.5 px-2.5 py-1 rounded transition"
                  style={{
                    background: 'var(--accent-soft)',
                    color: 'var(--accent)',
                  }}
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Edit
                </button>
              )}
              {!editing && isOverridden && prompt.editable && (
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('Reset to default? The custom version stays in history but the compiled-in default will be used.')) {
                      resetMutation.mutate();
                    }
                  }}
                  disabled={resetMutation.isPending}
                  className="text-xs text-ink-200 hover:text-ink-strong inline-flex items-center gap-1.5 px-2 py-1 rounded transition disabled:opacity-50"
                  title="Revert to the compiled-in default. History is preserved."
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reset
                </button>
              )}
              {editing && (
                <>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="text-xs text-ink-200 hover:text-ink-strong inline-flex items-center gap-1.5 px-2 py-1 rounded transition"
                  >
                    <X className="w-3.5 h-3.5" />
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => saveMutation.mutate()}
                    disabled={!draftChanged || draftEmpty || saveMutation.isPending}
                    className="text-xs inline-flex items-center gap-1.5 px-2.5 py-1 rounded transition disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{
                      background: 'var(--accent)',
                      color: 'white',
                    }}
                  >
                    <Save className="w-3.5 h-3.5" />
                    {saveMutation.isPending ? 'Saving…' : 'Save'}
                  </button>
                </>
              )}
            </div>
          </div>

          {!prompt.editable && prompt.not_editable_reason && (
            <div className="px-5 py-3 text-xs flex items-start gap-2"
                 style={{
                   background: 'color-mix(in srgb, var(--status-info) 8%, transparent)',
                   color: 'var(--text-200)',
                   borderBottom: '1px solid var(--glass-border)',
                 }}>
              <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: 'var(--status-info)' }} />
              <span>{prompt.not_editable_reason}</span>
            </div>
          )}

          {showDiff && diffLines ? (
            <DiffView lines={diffLines} />
          ) : editing ? (
            <div className="px-5 py-4 space-y-3">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                spellCheck={false}
                className="w-full font-mono text-xs leading-relaxed p-3 rounded-lg resize-y"
                style={{
                  background: 'var(--bg-input)',
                  color: 'var(--text-50)',
                  border: '1px solid var(--glass-border-h)',
                  minHeight: '320px',
                  maxHeight: '600px',
                  outline: 'none',
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--glass-border-h)'; }}
              />
              <div>
                <label className="text-[10px] uppercase tracking-[0.18em] text-ink-400 block mb-1">
                  Change note (optional)
                </label>
                <input
                  type="text"
                  value={changeNote}
                  onChange={(e) => setChangeNote(e.target.value)}
                  placeholder="e.g. Tightened OFF-MARKET keyword filter to reduce false positives"
                  className="w-full text-xs p-2 rounded-lg"
                  style={{
                    background: 'var(--bg-input)',
                    color: 'var(--text-strong)',
                    border: '1px solid var(--glass-border-h)',
                    outline: 'none',
                  }}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-ink-400 font-mono tabular-nums">
                <span>
                  {draft.length.toLocaleString()} chars · ~{Math.round(draft.length / 4).toLocaleString()} tokens
                  {draftChanged && (
                    <span className="ml-3" style={{ color: 'var(--accent)' }}>
                      {draft.length - activeBody.length >= 0 ? '+' : ''}
                      {(draft.length - activeBody.length).toLocaleString()} vs current
                    </span>
                  )}
                </span>
                {draftEmpty && (
                  <span style={{ color: 'var(--status-err)' }}>Body cannot be empty</span>
                )}
              </div>
              {saveError && (
                <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg"
                     style={{
                       background: 'color-mix(in srgb, var(--status-err) 10%, transparent)',
                       color: 'var(--status-err)',
                       border: '1px solid color-mix(in srgb, var(--status-err) 30%, transparent)',
                     }}>
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  {saveError}
                </div>
              )}
            </div>
          ) : (
            <pre
              className="px-5 py-4 text-xs font-mono leading-relaxed overflow-x-auto whitespace-pre-wrap break-words"
              style={{
                background: 'var(--bg-input)',
                color: 'var(--text-50)',
                maxHeight: '480px',
                overflowY: 'auto',
              }}
            >
              {activeBody}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

function DiffView({ lines }: { lines: Array<{ left: string | null; right: string | null; changed: boolean }> }) {
  return (
    <div className="grid grid-cols-2 text-xs font-mono overflow-x-auto"
         style={{ maxHeight: '600px', overflowY: 'auto' }}>
      <div className="border-r" style={{ borderColor: 'var(--glass-border)' }}>
        <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-ink-400 sticky top-0"
             style={{ background: 'var(--bg-hover)', borderBottom: '1px solid var(--glass-border)' }}>
          Default
        </div>
        {lines.map((l, i) => (
          <div key={i}
               className="px-3 py-0.5 whitespace-pre-wrap break-words leading-relaxed"
               style={{
                 background: l.changed && l.left !== null
                   ? 'color-mix(in srgb, var(--status-err) 8%, transparent)'
                   : 'transparent',
                 color: l.left === null ? 'var(--text-400)' : 'var(--text-50)',
                 minHeight: '1.5em',
               }}>
            {l.left ?? ' '}
          </div>
        ))}
      </div>
      <div>
        <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-ink-400 sticky top-0"
             style={{ background: 'var(--bg-hover)', borderBottom: '1px solid var(--glass-border)' }}>
          Current
        </div>
        {lines.map((l, i) => (
          <div key={i}
               className="px-3 py-0.5 whitespace-pre-wrap break-words leading-relaxed"
               style={{
                 background: l.changed && l.right !== null
                   ? 'color-mix(in srgb, var(--status-ok) 10%, transparent)'
                   : 'transparent',
                 color: l.right === null ? 'var(--text-400)' : 'var(--text-50)',
                 minHeight: '1.5em',
               }}>
            {l.right ?? ' '}
          </div>
        ))}
      </div>
    </div>
  );
}
